import { useCallback, useMemo, useRef } from 'react';
import sortBy from 'lodash/sortBy';

import {
  CONTENT_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
} from 'global/constants';
import { DIRECTION_LEFT } from 'webclient.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { ACCEPT_TYPE_IMAGE } from 'lib/form/file-dropper/file-dropper.constants';
import {
  FileDropperContextProvider,
  useFileDropperContext,
} from 'lib/form/file-dropper/file-dropper.context';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { EditProps, IMAGE_FIELD } from './slideshow.constants';
import SlideshowEditPreview from './slideshow-edit-preview';
import customNanoId from 'lib/nanoid/nanoid';
import { addImageToSlideshow } from './slideshow.utils';
import {
  ComponentDimensions,
  useComponentDimensions,
} from '../shared/component-height/component-dimensions';
import {
  useAddImageToNewComponent,
  useFindComponentDimensions,
} from '../component.hooks';
import ComponentAltTextButton from '../shared/component-alt-text/component-alt-text-button';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ComponentImageModal from '../shared/component-image-modal/component-image-modal';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';
import { PAGE_CONTROL_HEIGHT } from './slideshow-view.style';
import * as Styled from './slideshow.style';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const SlideshowEdit = (props: EditProps) => {
  const {
    id: slideshowComponentId,
    sectionId,
    slideshowImages,
    contentId,
    properties,
  } = props;

  const dropzoneRef = useRef<HTMLInputElement | null>(null);

  const { activeComponent } = useActivityBuilderContext();
  const { clearFile } = useFileDropperContext(IMAGE_FIELD);
  const { elementDimensionLabel, width, height } =
    useFindComponentDimensions(slideshowComponentId);

  const { contentType } = useGetInteractiveLessonParams();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const sortedSlides = useMemo(
    () => sortBy(slideshowImages, 'sort'),
    [slideshowImages]
  );

  const { dimensions, setDimensions } = useComponentDimensions({
    defaultValue: properties.dimensionData,
  });

  const { addImageToNewComponent, client } = useAddImageToNewComponent({
    onCompleted: clearFile,
  });

  const { loading, updateComponentProperties } =
    useBatchComponentUpdateContext();

  const handleFileChange = useCallback(
    (key: string, fileName: string) => {
      const componentId = customNanoId();
      const sort = getReorderSort([], 0, DIRECTION_LEFT);

      addImageToNewComponent({
        variables: {
          input: {
            contentId,
            contentType,
            sectionId,
            componentId,
            parentDetails: {
              parentComponentId: slideshowComponentId,
            },
            componentData: {
              type: ACTIVITIES_COMPONENT_TYPE_IMAGE,
              properties: {
                stimulusId: null,
                showCaptionOnLesson: false,
                dimensionData: {
                  width: 1073,
                  height: 352,
                },
              },
              sort,
            },
            imageInput: {
              key,
              imageType: CONTENT_IMAGE,
              data: null,
              fileName,
            },
          },
        },
      }).then(() => {
        addImageToSlideshow({
          client,
          componentId,
          slideshowComponentId,
        });
      });
    },
    [
      client,
      addImageToNewComponent,
      contentId,
      sectionId,
      slideshowComponentId,
      contentType,
    ]
  );

  const handleHeightUpdate = useCallback(
    (width: number, height: number) => {
      const dimensionData = {
        ...properties.dimensionData,
        width,
        height: Math.floor(height),
      };
      updateComponentProperties({
        input: {
          sectionId,
          componentId: slideshowComponentId,
          componentData: {
            properties: {
              dimensionData,
            },
          },
        },
        properties: {
          ...properties,
          dimensionData,
        },
        typename: 'ActivitiesSlideshowComponent',
      });
    },
    [sectionId, slideshowComponentId, properties, updateComponentProperties]
  );

  return (
    <>
      {sortedSlides.length > 0 ? (
        <ComponentDimensions
          id={slideshowComponentId}
          showHandle={activeComponent?.id === slideshowComponentId}
          defaultValue={properties.dimensionData}
          onResize={(width, height) => {
            setDimensions({ width, height });
          }}
          onResizeEnd={handleHeightUpdate}
        >
          <SlideshowEditPreview
            {...props}
            properties={{
              ...props.properties,
              dimensionData: {
                ...props.properties.dimensionData,
                width: dimensions.width,
                height: dimensions.height,
              },
            }}
            renderActions={slide =>
              slide ? (
                <>
                  {isVisible && (
                    <ComponentImageModal
                      isImageRequired
                      loading={loading}
                      containerDimensions={{
                        width: width,
                        height: height - PAGE_CONTROL_HEIGHT,
                      }}
                      image={slide.properties.image || undefined}
                      sourceUrl={slide.properties.sourceUrl}
                      altText={slide.properties.altText}
                      cropData={
                        slide.properties.cropData
                          ? removeTypenameFromObject(slide.properties.cropData)
                          : null
                      }
                      closeModal={closeModal}
                      onUpdateData={data =>
                        updateComponentProperties({
                          input: {
                            sectionId: slide.sectionId,
                            componentId: slide.id,
                            componentData: {
                              properties: {
                                sourceUrl: data.sourceUrl,
                                cropData: data.cropData,
                                altText: data.altText,
                                image: data.image
                                  ? {
                                      imageType: CONTENT_IMAGE,
                                      key: data.image.key,
                                      fileName: data.image.fileName,
                                      data: {
                                        flip: data.image.data?.flip || null,
                                      },
                                    }
                                  : null,
                              },
                            },
                          },
                          typename: 'ActivitiesSlideshowComponent',
                          isSaveImmediate: true,
                          onCompleted: () => closeModal(),
                        })
                      }
                    />
                  )}
                  <ComponentAltTextButton
                    hasAltText={!!slide.properties.altText}
                    onClick={openModal}
                  />
                </>
              ) : null
            }
          />
        </ComponentDimensions>
      ) : (
        <Styled.SlideshowUploadWrapper>
          <Styled.SlideshowUploadAspectRatio>
            <Styled.SlideshowUpload>
              <FileDropperImage
                ref={dropzoneRef}
                name={IMAGE_FIELD}
                inputDataSelector={IMAGE_FIELD}
                accept={[ACCEPT_TYPE_IMAGE]}
                additionalLabel={elementDimensionLabel}
                onChange={handleFileChange}
              />
            </Styled.SlideshowUpload>
          </Styled.SlideshowUploadAspectRatio>
        </Styled.SlideshowUploadWrapper>
      )}
    </>
  );
};

const SlideshowEditWrapper = (props: EditProps) => (
  <FileDropperContextProvider>
    <SlideshowEdit {...props} />
  </FileDropperContextProvider>
);

export default SlideshowEditWrapper;
